import { useState } from 'react';
import { Printer, ChevronDown, ChevronRight, ArrowRight, Clock, Search, HelpCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';

const modules = [
  {
    id: 1,
    title: 'Parte Diario Terreno',
    route: 'RRHH > Parte Diario Terreno',
    summary: 'Planilla diaria donde el supervisor registra asistencia, horarios, asignación de HH a OTs y pedidos de almuerzo.',
    phases: [
      {
        name: 'Fase 1 — Listado de Personal (mañana)',
        steps: [
          'Seleccionar **fecha** y **OT principal** en la parte superior.',
          'Si es el primer parte del día, hacer clic en **"Cargar personal día anterior"** para traer la cuadrilla del día previo.',
          'Para **agregar un trabajador nuevo**: usar el buscador en la parte inferior, seleccionar el nombre y se agrega a la lista.',
          'Para cada trabajador, registrar: **Hora entrada/salida AM**, **Hora entrada/salida PM**, **Almuerzo** (Sí/No y tipo), **Ausencia** (motivo si no vino).',
          'El parte se **guarda automáticamente** cada vez que se modifica un campo.',
        ],
      },
      {
        name: 'Fase 2 — Asignar HH (tarde/cierre)',
        steps: [
          'Hacer clic en **"Asignar HH"** en la parte superior para cambiar a la segunda fase.',
          'Para cada trabajador, distribuir las horas trabajadas del día en una o más OTs: seleccionar **OT**, ingresar **Horas**.',
          'Se pueden agregar **múltiples OTs** por trabajador (ej: 4h OT 3105 + 4h OT 3200).',
          'El botón **"Asignar HH uno a uno"** permite cargar horario estándar (8 HN) a cada trabajador individualmente.',
          'Al terminar, hacer clic en **"Cerrar Parte"** para bloquear la edición y generar los registros de Control HH.',
        ],
      },
      {
        name: 'Firmas',
        steps: [
          'En la sección inferior del parte, buscar y seleccionar **Supervisor** del listado de trabajadores.',
          'Buscar y seleccionar **Prevencionista** del listado de trabajadores.',
        ],
      },
    ],
    tips: [
      'El "carry-over" trae la cuadrilla del parte más reciente, no necesariamente del día anterior.',
      'Si necesita reabrir un parte cerrado, consulte con el administrador.',
    ],
  },
  {
    id: 2,
    title: 'Resumen Almuerzos (Pedido)',
    route: 'RRHH > Resumen Almuerzos',
    summary: 'Consolidado diario de pedidos de almuerzo agrupados por OT. Permite enviar el pedido al concesionario de casino.',
    phases: [
      {
        name: 'Consultar y enviar pedido',
        steps: [
          'Seleccionar **fecha** (por defecto muestra hoy).',
          'El sistema muestra automáticamente un resumen con: cantidad de almuerzos por tipo (Normal, Vegetariano, etc.), agrupados por OT principal, y total general.',
          'Para enviar el pedido al concesionario: hacer clic en **"Enviar Almuerzo"**.',
          'El sistema envía un correo automático al email del concesionario configurado para cada ubicación.',
          'El botón cambia a **"Enviado"** una vez procesado.',
          'Si necesita reenviar (ej: cambió la cantidad), hacer clic en **"Reenviar"**.',
        ],
      },
      {
        name: 'Configurar concesionarios',
        steps: [
          'En la misma página hay un botón para **configurar los emails** de los concesionarios por ubicación/cliente.',
          'Agregar nombre del concesionario, ubicación y email de destino.',
        ],
      },
    ],
    tips: [
      'El correo se envía automáticamente al concesionario asignado según la ubicación del trabajador.',
      'Revise que los datos de almuerzo estén completos en el Parte Diario antes de enviar.',
    ],
  },
  {
    id: 3,
    title: 'Resumen Casino',
    route: 'RRHH > Resumen Casino',
    summary: 'Vista mensual en cuadrícula que muestra los almuerzos servidos por día, agrupados por casino/cliente.',
    phases: [
      {
        name: 'Consultar resumen',
        steps: [
          'Seleccionar **mes y año**.',
          'El sistema muestra una tabla con: **Filas** = cada casino/ubicación, **Columnas** = días del mes (1 al 31).',
          'Cada celda muestra la cantidad de almuerzos servidos ese día en ese casino.',
          'Se muestran **totales** por fila (total del casino en el mes) y por columna (total del día).',
        ],
      },
    ],
    tips: [
      'Útil para la conciliación mensual con los proveedores de casino.',
    ],
  },
  {
    id: 4,
    title: 'Control HH (Informe por OT)',
    route: 'RRHH > Control HH',
    summary: 'Vista unificada de todas las horas hombre cargadas al sistema. Consulta por OT, trabajador o fecha con drill-down en 3 niveles.',
    phases: [
      {
        name: 'Navegación por niveles',
        steps: [
          '**Nivel 1 — Vista general**: muestra resumen de HH por OT (total HN, HEX, por período).',
          'Hacer clic en una **OT** para ir al **Nivel 2**: detalle de todos los trabajadores que cargaron horas a esa OT.',
          'Hacer clic en un **trabajador** para ir al **Nivel 3**: detalle día a día de las horas cargadas.',
        ],
      },
      {
        name: 'Filtros disponibles',
        steps: [
          'Rango de **fechas** (desde - hasta).',
          '**OT** específica.',
          '**Trabajador** específico.',
        ],
      },
    ],
    tips: [
      'Los datos provienen de los Partes Diarios cerrados y también de la importación desde Access.',
    ],
  },
  {
    id: 5,
    title: 'Reloj Control',
    route: 'RRHH > Reloj Control',
    summary: 'Registro mensual de marcas del reloj control (hora entrada/salida) por trabajador. Se usa para comparar con las HH cargadas.',
    phases: [
      {
        name: 'Consultar e ingresar marcas',
        steps: [
          'Seleccionar **mes/año** y **OT**.',
          'El sistema muestra una grilla con: **Filas** = trabajadores, **Columnas** = días del mes.',
          'Cada celda muestra la hora de entrada y salida marcadas en el reloj.',
          'Para **ingresar marcas manualmente**: hacer clic en la celda del día/trabajador.',
          'Ingresar **hora entrada** y **hora salida**, luego **Guardar**.',
        ],
      },
    ],
    tips: [
      'Los viernes tienen hora de salida configurable (ej: 17:00 en vez de 18:00). Configurar en **Feriados y Horarios**.',
      'Las marcas se usan como referencia en el Control de Errores.',
    ],
  },
  {
    id: 6,
    title: 'Control de Errores',
    route: 'RRHH > Control de Errores',
    summary: 'Comparación automática entre las marcas del Reloj Control y las HH cargadas en el Parte Diario. Detecta inconsistencias.',
    phases: [
      {
        name: 'Revisar discrepancias',
        steps: [
          'Seleccionar **mes/año** y **OT**.',
          'El sistema compara automáticamente: **Reloj Control** (marcas) vs **Control HH** (horas cargadas).',
          'Las **diferencias** se resaltan en color: trabajador marcó entrada pero no tiene HH, o viceversa, o hay diferencias significativas.',
          'Permite **corregir** directamente desde esta pantalla.',
        ],
      },
    ],
    tips: [
      'Revise este informe antes de cerrar el mes para asegurar consistencia.',
    ],
  },
  {
    id: 7,
    title: 'Panel de Personal',
    route: 'RRHH > Panel de Personal',
    summary: 'Dashboard en tiempo real con distribución del personal activo, agrupado por cliente/OT. Tiene 3 vistas: Diaria, Semanal y Mensual.',
    phases: [
      {
        name: 'Vista Diaria',
        steps: [
          'Muestra **columnas por cliente** (ej: Oxiquim, CHS, Agher).',
          'Cada columna lista los trabajadores **presentes hoy** según los partes diarios.',
          'Incluye contadores de: total presentes, ausencias (con motivo) y atrasos.',
        ],
      },
      {
        name: 'Vista Semanal',
        steps: [
          'Muestra resumen de la **semana actual**.',
          'Días trabajados por persona.',
        ],
      },
      {
        name: 'Vista Mensual',
        steps: [
          'Resumen del **mes completo**.',
          'Totales acumulados por cliente.',
        ],
      },
    ],
    tips: [
      'La información se actualiza en tiempo real según los partes diarios registrados.',
    ],
  },
  {
    id: 8,
    title: 'Asistencia Mensual',
    route: 'RRHH > Asistencia Mensual',
    summary: 'Planilla mensual tipo spreadsheet para calcular los "Días Trabajados" del mes con clasificaciones manuales.',
    phases: [
      {
        name: 'Consultar la grilla',
        steps: [
          'Seleccionar **mes/año** (formato MM/AA) y opcionalmente filtrar por **OT**.',
          'El sistema muestra una grilla: **Filas** = trabajadores activos (nombre, ficha, RUT, cargo), **Columnas** = días del mes (1 al 31).',
          'La **última columna** muestra **Días Trabajados (D.Trab)** calculados automáticamente.',
        ],
      },
      {
        name: 'Clasificar días manualmente',
        steps: [
          'Hacer **clic en cualquier celda** de la grilla.',
          'Seleccionar la clasificación del menú desplegable.',
          'La celda cambia de color y el cálculo de Días Trabajados se actualiza automáticamente.',
        ],
      },
      {
        name: 'Exportar',
        steps: [
          'Hacer clic en el botón **"Exportar Excel"** para generar un archivo .xlsx descargable con todo el formato.',
        ],
      },
    ],
    classifications: [
      { code: 'A', color: 'bg-green-100 text-green-800', desc: 'Asistió (tiene HH cargadas)' },
      { code: '*', color: 'bg-gray-100 text-gray-500', desc: 'Sin información' },
      { code: 'P', color: 'bg-blue-100 text-blue-800', desc: 'Permiso' },
      { code: 'L', color: 'bg-orange-100 text-orange-800', desc: 'Licencia Médica' },
      { code: 'V', color: 'bg-purple-100 text-purple-800', desc: 'Vacaciones (llena semana completa)' },
      { code: 'F', color: 'bg-red-100 text-red-800', desc: 'Falla (sin justificación)' },
      { code: 'O', color: 'bg-gray-200 text-gray-700', desc: 'Otro' },
      { code: 'FE', color: 'bg-yellow-100 text-yellow-800', desc: 'Feriado (toda la columna)' },
      { code: 'X', color: 'bg-gray-800 text-white', desc: 'No ingresado / Sin contrato' },
      { code: 'Q', color: 'bg-gray-900 text-white', desc: 'Finiquito (llena hacia adelante)' },
      { code: 'T', color: 'bg-cyan-100 text-cyan-800', desc: 'Trato' },
    ],
    formula: 'D.Trab = 30 - X/Q - P - F - L - T - Días laborables sin info - Equivalente atraso',
    formulaNotes: [
      'Base siempre 30 días.',
      'Vacaciones (V), Feriados (FE) y Otros (O) **NO** descuentan.',
      'Equivalente atraso = Total minutos atraso / 540 (una jornada completa).',
    ],
    tips: [
      'Vacaciones (V) llena automáticamente la semana laboral completa (Lu-Vi).',
      'Finiquito (Q) llena hacia adelante todos los días restantes del mes.',
      'Solo se muestran trabajadores activos.',
    ],
  },
  {
    id: 9,
    title: 'Trabajadores',
    route: 'RRHH > Trabajadores',
    summary: 'Maestro de trabajadores. CRUD completo para administrar el personal de la empresa.',
    phases: [
      {
        name: 'Gestionar trabajadores',
        steps: [
          '**Listado**: muestra todos los trabajadores con búsqueda por nombre, RUT o número.',
          '**Filtrar**: por estado (Activos / Inactivos / Todos).',
          '**Crear trabajador**: clic en "Agregar Trabajador", completar Número, Nombre, RUT, Cargo, Área, Estado. Guardar.',
          '**Editar**: clic en el trabajador, modificar campos y Guardar.',
        ],
      },
    ],
    tips: [
      'Los trabajadores se sincronizan automáticamente desde Access cada 15 minutos.',
      'Los cambios hechos en Access se reflejan automáticamente en la web.',
    ],
  },
  {
    id: 10,
    title: 'Dashboard RRHH',
    route: 'RRHH > Dashboard RRHH',
    summary: 'Vista ejecutiva con indicadores clave del módulo de Recursos Humanos.',
    phases: [
      {
        name: 'Indicadores disponibles',
        steps: [
          '**6 KPIs principales**: Total trabajadores activos, Presentes hoy, Ausentes hoy, HH cargadas del mes, Partes diarios del mes, Atrasos del mes.',
          '**Gráfico por Área de Origen**: distribución de personal por área.',
          '**Top Trabajadores**: los que más HH han cargado.',
          '**Top OTs**: las OTs con más HH consumidas.',
        ],
      },
    ],
    tips: [
      'Los datos se actualizan en tiempo real.',
    ],
  },
];

const dailyFlow = [
  { time: 'MAÑANA', who: 'Supervisor en terreno', steps: [
    'Abrir Parte Diario y crear parte del día.',
    'Cargar personal del día anterior.',
    'Marcar asistencia, horarios, ausencias.',
    'Marcar almuerzos.',
  ]},
  { time: 'MEDIA MAÑANA', who: 'Bodega / Oficina', steps: [
    'Resumen Almuerzos: enviar pedido al concesionario.',
  ]},
  { time: 'TARDE', who: 'Supervisor en terreno', steps: [
    'Parte Diario Fase 2: Asignar HH por OT.',
    'Cerrar Parte.',
  ]},
  { time: 'FIN DE MES', who: 'Oficina RRHH', steps: [
    'Reloj Control: ingresar marcas del mes.',
    'Control de Errores: revisar diferencias.',
    'Asistencia Mensual: clasificar días especiales (V, P, L, F, etc.).',
    'Exportar Excel y enviar a Remuneraciones.',
  ]},
];

function RenderBold(text) {
  return text.split(/\*\*(.*?)\*\*/g).map((part, j) =>
    j % 2 === 1 ? <strong key={j} className="text-gray-900">{part}</strong> : part
  );
}

function ModuleCard({ mod, isOpen, onToggle }) {
  return (
    <div className="border border-gray-200 rounded-lg bg-white print:border-gray-300 print:break-inside-avoid" data-testid={`rrhh-proc-module-${mod.id}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-gray-50 transition-colors print:hover:bg-white"
        data-testid={`rrhh-proc-toggle-${mod.id}`}
      >
        <span className="flex items-center justify-center w-8 h-8 rounded-full bg-[#003366] text-white text-sm font-bold shrink-0">
          {mod.id}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-[15px] text-gray-900">{mod.title}</h3>
          <p className="text-xs text-gray-500 mt-0.5 truncate">{mod.route}</p>
        </div>
        <span className="print:hidden shrink-0">
          {isOpen ? <ChevronDown className="w-5 h-5 text-gray-400" /> : <ChevronRight className="w-5 h-5 text-gray-400" />}
        </span>
      </button>

      <div className={`${isOpen ? 'block' : 'hidden'} print:!block border-t border-gray-100 p-4 pt-3 space-y-4`}>
        <p className="text-sm text-gray-600">{mod.summary}</p>

        {mod.phases.map((phase, pi) => (
          <div key={pi}>
            <h4 className="text-sm font-semibold text-[#003366] mb-2">{phase.name}</h4>
            <ol className="space-y-1.5 ml-1">
              {phase.steps.map((s, i) => (
                <li key={i} className="flex gap-2 text-sm">
                  <span className="text-[#003366] font-semibold shrink-0 w-5 text-right">{i + 1}.</span>
                  <span className="text-gray-700">{RenderBold(s)}</span>
                </li>
              ))}
            </ol>
          </div>
        ))}

        {/* Classifications table for Asistencia Mensual */}
        {mod.classifications && (
          <div>
            <h4 className="text-sm font-semibold text-[#003366] mb-2">Significado de las celdas</h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
              {mod.classifications.map((c) => (
                <div key={c.code} className="flex items-center gap-2 text-xs">
                  <span className={`inline-flex items-center justify-center w-7 h-6 rounded font-bold text-[11px] ${c.color}`}>
                    {c.code}
                  </span>
                  <span className="text-gray-700">{c.desc}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Formula for Asistencia Mensual */}
        {mod.formula && (
          <div className="bg-slate-50 border border-slate-200 rounded p-3">
            <h4 className="text-sm font-semibold text-[#003366] mb-1">Fórmula Días Trabajados</h4>
            <code className="text-xs bg-slate-100 px-2 py-1 rounded block mb-2 text-slate-800">{mod.formula}</code>
            <ul className="space-y-0.5 ml-3 list-disc">
              {mod.formulaNotes.map((n, i) => (
                <li key={i} className="text-xs text-gray-600">{RenderBold(n)}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Tips */}
        {mod.tips && mod.tips.length > 0 && (
          <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded p-2.5">
            <HelpCircle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-semibold text-amber-800 mb-1">Consejos</p>
              <ul className="space-y-0.5 ml-3 list-disc">
                {mod.tips.map((t, i) => (
                  <li key={i} className="text-xs text-amber-700">{RenderBold(t)}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProcedimientosRRHH() {
  const allIds = modules.map(m => m.id);
  const [openModules, setOpenModules] = useState(new Set(allIds));
  const [searchTerm, setSearchTerm] = useState('');

  const toggle = (id) => {
    setOpenModules(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const expandAll = () => setOpenModules(new Set(allIds));
  const collapseAll = () => setOpenModules(new Set());

  const filtered = searchTerm.trim()
    ? modules.filter(m =>
        m.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.summary.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : modules;

  return (
    <div className="max-w-4xl mx-auto p-6 print:p-2 print:max-w-none" data-testid="procedimientos-rrhh-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6 print:mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 print:text-xl">Procedimientos RRHH</h1>
          <p className="text-sm text-gray-500 mt-1">Manual de ayuda para cada submódulo de Recursos Humanos</p>
        </div>
        <div className="flex gap-2 print:hidden">
          <Button variant="outline" size="sm" onClick={collapseAll} data-testid="collapse-all-btn">
            Colapsar
          </Button>
          <Button variant="outline" size="sm" onClick={expandAll} data-testid="expand-all-btn">
            Expandir
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-2"
            data-testid="print-rrhh-btn"
            onClick={() => { expandAll(); setTimeout(() => window.print(), 200); }}
          >
            <Printer className="w-4 h-4" /> Imprimir
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4 print:hidden">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <input
          type="text"
          placeholder="Buscar submódulo..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#003366]/20 focus:border-[#003366]"
          data-testid="rrhh-proc-search"
        />
      </div>

      {/* Quick nav pills */}
      <div className="flex flex-wrap gap-1.5 mb-5 print:hidden">
        {modules.map(m => (
          <button
            key={m.id}
            onClick={() => {
              setOpenModules(prev => new Set([...prev, m.id]));
              document.querySelector(`[data-testid="rrhh-proc-module-${m.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }}
            className="text-xs px-2.5 py-1 rounded-full bg-[#003366]/5 text-[#003366] hover:bg-[#003366]/15 transition-colors font-medium"
            data-testid={`rrhh-proc-pill-${m.id}`}
          >
            {m.id}. {m.title}
          </button>
        ))}
      </div>

      {/* Module Cards */}
      <div className="space-y-3 print:space-y-2">
        {filtered.map(mod => (
          <ModuleCard
            key={mod.id}
            mod={mod}
            isOpen={openModules.has(mod.id)}
            onToggle={() => toggle(mod.id)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-center text-sm text-gray-400 py-8">No se encontraron submódulos con "{searchTerm}"</p>
        )}
      </div>

      {/* Daily Flow */}
      <div className="mt-8 print:mt-4">
        <h2 className="text-lg font-bold text-gray-900 mb-4">Flujo Diario Típico</h2>
        <div className="space-y-3">
          {dailyFlow.map((block, bi) => (
            <div key={bi} className="flex gap-3 items-start" data-testid={`daily-flow-${bi}`}>
              <div className="shrink-0 w-32 sm:w-40">
                <div className="flex items-center gap-1.5">
                  <Clock className="w-3.5 h-3.5 text-[#003366]" />
                  <span className="text-xs font-bold text-[#003366] uppercase">{block.time}</span>
                </div>
                <span className="text-[11px] text-gray-500 ml-5">{block.who}</span>
              </div>
              <div className="flex-1 border-l-2 border-[#003366]/20 pl-4">
                <ol className="space-y-1">
                  {block.steps.map((s, i) => (
                    <li key={i} className="flex gap-2 text-sm">
                      <ArrowRight className="w-3.5 h-3.5 text-[#003366]/50 shrink-0 mt-0.5" />
                      <span className="text-gray-700">{s}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800 print:mt-4">
        <strong>Nota:</strong> Los datos de trabajadores y horas históricas se sincronizan automáticamente desde Access cada 15 minutos.
        Para configurar feriados y horarios especiales (ej: viernes), ir a <strong>Configuración &gt; Feriados y Horarios</strong>.
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          nav, aside, header, [data-testid="mobile-menu-toggle"],
          .print\\:hidden { display: none !important; }
          body { font-size: 12px; }
          main { margin: 0 !important; padding: 0 !important; }
          .print\\:break-inside-avoid { break-inside: avoid; }
          .print\\:\\!block { display: block !important; }
        }
      `}</style>
    </div>
  );
}
